import { handleDynamicFontResize } from "../processFont";
import {
  canvasDeltaToPdfDelta,
  canvasToPdfPoints,
  pdfRectToCanvasRect,
  resizeCanvasRect,
} from "../../../../utils/coordinateUtils";

const resizeSnapshotsByMousePosition = new WeakMap();
const resizeGeometryByMousePosition = new WeakMap();
const insertedTextResizeSnapshotsByMousePosition = new WeakMap();
const selectionGeometryByStartPoint = new WeakMap();

// Rebuild only when field geometry, the page, or the viewport changes.
const getSelectionGeometry = (startPoint, fields, document, pageNumber) => {
  const keys = [
    fields,
    pageNumber,
    document.viewport,
    document.actualHeight,
    document.scaleFactor,
    document.pageXMin,
    document.pageYMin,
  ];
  const cached = selectionGeometryByStartPoint.get(startPoint);
  if (
    cached &&
    keys.every((key, index) => Object.is(key, cached.keys[index]))
  ) {
    return cached.rects;
  }
  const rects = fields
    .filter((field) => Number(field?.page_num ?? field?.page) === pageNumber)
    .map((field) => ({
      field,
      rect: pdfRectToCanvasRect(
        field,
        document.viewport || document.actualHeight || 792,
        document.scaleFactor || 1,
        document.pageXMin || 0,
        document.pageYMin || 0,
      ),
    }));
  selectionGeometryByStartPoint.set(startPoint, { keys, rects });
  return rects;
};

export const handleMouseMove = (
  e,
  currentProcessingFile,
  setCurrentProcessingFile,
  pdfContainerRef,
  resizing,
  initialMousePosition,
  initialInputSize,
  inputPosition,
  resizeDirection,
  moving,
  isDrawing,
  startPoint,
  setRect,
  isSelecting,
  freeSelectStartPoint,
  setSelectionRect,
  pageNumber,
) => {
  if (!resizing && !moving && !isDrawing && !isSelecting) return;

  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pdfSize /
    currentProcessingFile?.document?.tempFields?.currentDocument?.actualWidth;

  const containerRect = pdfContainerRef?.current?.getBoundingClientRect();
  if (!containerRect) return;

  const pdfLeft = 0;
  const pdfRight = containerRect.width - 10;
  const pdfBottom = containerRect.height;

  if (resizing && initialMousePosition?.insertedTextEditId) {
    const tempFields = currentProcessingFile?.document?.tempFields;
    const currentDocument = tempFields?.currentDocument;
    const editId = initialMousePosition.insertedTextEditId;
    const insertedText = tempFields?.pdfTextEdits?.find(
      (edit) =>
        edit.edit_id === editId &&
        edit.isEditorAdded === true &&
        edit.kind === "inserted" &&
        edit.action !== "delete" &&
        Number(edit.page) === Number(pageNumber),
    );
    if (
      !currentDocument ||
      !insertedText?.rect ||
      tempFields.selectedPdfTextItem?.id !== insertedText.target_item_id ||
      tempFields.editingInsertedTextId ||
      !inputPosition ||
      !initialInputSize ||
      !["nw", "n", "ne", "sw", "s", "se", "w", "e"].includes(resizeDirection)
    ) {
      return;
    }

    const initialRect = {
      left: Number(inputPosition.x),
      top: Number(inputPosition.y),
      width: Number(initialInputSize.width),
      height: Number(initialInputSize.height),
    };
    const dx = e.clientX - initialMousePosition.x;
    const dy = e.clientY - initialMousePosition.y;
    const pageWidth = Number(currentDocument.actualWidth);
    const pageHeight = Number(currentDocument.actualHeight);
    const pageXMin = Number(currentDocument.pageXMin ?? 0);
    const pageYMin = Number(currentDocument.pageYMin ?? 0);
    const displayScale = Number(
      currentDocument.scaleFactor || scaleFactor || 1,
    );
    if (
      ![
        ...Object.values(initialRect),
        dx,
        dy,
        pageWidth,
        pageHeight,
        pageXMin,
        pageYMin,
        displayScale,
      ].every(Number.isFinite) ||
      initialRect.width <= 0 ||
      initialRect.height <= 0 ||
      pageWidth <= 0 ||
      pageHeight <= 0 ||
      displayScale <= 0
    ) {
      return;
    }

    let snapshot =
      insertedTextResizeSnapshotsByMousePosition.get(initialMousePosition);
    if (!snapshot) {
      snapshot = { document: currentDocument, rect: { ...insertedText.rect } };
      insertedTextResizeSnapshotsByMousePosition.set(
        initialMousePosition,
        snapshot,
      );
    }
    if (snapshot.document !== currentDocument) return;

    // Resize visually, so all eight handles keep their screen direction even
    // on rotated pages. Convert the resulting rectangle back to PDF points.
    const pageRect = pdfRectToCanvasRect(
      { X: pageXMin, Y: pageYMin, W: pageWidth, H: pageHeight },
      currentDocument.viewport || pageHeight,
      displayScale,
      pageXMin,
      pageYMin,
    );
    const nextCanvasRect = resizeCanvasRect(
      initialRect,
      dx,
      dy,
      resizeDirection,
      {
        left: pageRect.left,
        top: pageRect.top,
        right: pageRect.left + pageRect.width,
        bottom: pageRect.top + pageRect.height,
      },
      Math.min(5, initialRect.width, initialRect.height),
    );
    const unchanged = Object.keys(initialRect).every(
      (key) => Math.abs(nextCanvasRect[key] - initialRect[key]) < 1e-8,
    );
    const nextPdfRect = unchanged
      ? null
      : canvasToPdfPoints(
          nextCanvasRect.left,
          nextCanvasRect.top,
          nextCanvasRect.width,
          nextCanvasRect.height,
          currentDocument.viewport || displayScale,
          72,
          pageHeight,
          pageXMin,
          pageYMin,
        );
    const rect = nextPdfRect
      ? {
          ...snapshot.rect,
          X: nextPdfRect.x,
          Y: nextPdfRect.y,
          W: nextPdfRect.width,
          H: nextPdfRect.height,
        }
      : { ...snapshot.rect };
    if (
      ![rect.X, rect.Y, rect.W, rect.H].every(Number.isFinite) ||
      rect.W <= 0 ||
      rect.H <= 0
    ) {
      return;
    }

    setCurrentProcessingFile((prevFile) => {
      const temp = prevFile?.document?.tempFields;
      if (
        !temp ||
        temp.currentDocument !== snapshot.document ||
        temp.selectedPdfTextItem?.id !== insertedText.target_item_id ||
        temp.editingInsertedTextId
      ) {
        return prevFile;
      }
      const currentEdit = temp.pdfTextEdits?.find(
        (edit) =>
          edit.edit_id === editId &&
          edit.target_item_id === insertedText.target_item_id &&
          edit.isEditorAdded === true &&
          edit.kind === "inserted" &&
          edit.action !== "delete" &&
          Number(edit.page) === Number(pageNumber),
      );
      if (
        !currentEdit?.rect ||
        ["X", "Y", "W", "H"].every((key) => currentEdit.rect[key] === rect[key])
      ) {
        return prevFile;
      }

      // Resize only the box; keep text, font settings, and insertion identity.
      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...temp,
            pdfTextEdits: temp.pdfTextEdits.map((edit) =>
              edit === currentEdit ? { ...currentEdit, rect } : edit,
            ),
            selectedPdfTextItem: { ...temp.selectedPdfTextItem, rect },
          },
        },
      };
    });
  } else if (resizing) {
    const dx = e.clientX - initialMousePosition.x;
    const dy = e.clientY - initialMousePosition.y;
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const viewport = currentDocument.viewport;
    const resizeBounds = {
      left: pdfLeft,
      top: 0,
      right: Number(viewport?.width) || pdfRight,
      bottom: Number(viewport?.height) || pdfBottom,
    };
    const tempFields = currentProcessingFile?.document?.tempFields || {};
    const selectedFieldNames = new Set(
      [tempFields.selectedFormField, ...(tempFields.selectedExtraFields || [])]
        .filter(Boolean)
        .map((selectedField) => selectedField.name),
    );
    let resizeSnapshot =
      resizeSnapshotsByMousePosition.get(initialMousePosition);

    if (!resizeSnapshot) {
      resizeSnapshot = new Map(
        (tempFields.formFields || [])
          .filter((formField) => selectedFieldNames.has(formField.name))
          .map((formField) => [formField.name, formField]),
      );
      resizeSnapshotsByMousePosition.set(initialMousePosition, resizeSnapshot);
    }

    const geometryKeys = [
      resizeSnapshot,
      viewport,
      currentDocument.actualHeight,
      currentDocument.scaleFactor,
      scaleFactor,
      currentDocument.pageXMin,
      currentDocument.pageYMin,
      inputPosition.x,
      inputPosition.y,
      initialInputSize.width,
      initialInputSize.height,
    ];
    let geometry = resizeGeometryByMousePosition.get(initialMousePosition);
    if (
      !geometry ||
      !geometryKeys.every((key, index) => Object.is(key, geometry.keys[index]))
    ) {
      const initialCanvasRects = Array.from(resizeSnapshot.values()).map(
        (formField) => ({
          name: formField.name,
          rect: pdfRectToCanvasRect(
            formField,
            viewport || currentDocument.actualHeight || 792,
            currentDocument.scaleFactor || scaleFactor || 1,
            currentDocument.pageXMin || 0,
            currentDocument.pageYMin || 0,
          ),
        }),
      );
      const isGroupResize = initialCanvasRects.length > 1;
      const initialGroupRect = isGroupResize
        ? {
            left: Math.min(...initialCanvasRects.map(({ rect }) => rect.left)),
            top: Math.min(...initialCanvasRects.map(({ rect }) => rect.top)),
            width:
              Math.max(
                ...initialCanvasRects.map(({ rect }) => rect.left + rect.width),
              ) - Math.min(...initialCanvasRects.map(({ rect }) => rect.left)),
            height:
              Math.max(
                ...initialCanvasRects.map(({ rect }) => rect.top + rect.height),
              ) - Math.min(...initialCanvasRects.map(({ rect }) => rect.top)),
          }
        : {
            left: inputPosition.x,
            top: inputPosition.y,
            width: initialInputSize.width,
            height: initialInputSize.height,
          };
      geometry = {
        keys: geometryKeys,
        initialRectsByName: new Map(
          initialCanvasRects.map(({ name, rect }) => [name, rect]),
        ),
        isGroupResize,
        initialGroupRect,
      };
      resizeGeometryByMousePosition.set(initialMousePosition, geometry);
    }
    const { initialRectsByName, isGroupResize, initialGroupRect } = geometry;
    const resizedGroupRect = resizeCanvasRect(
      initialGroupRect,
      dx,
      dy,
      resizeDirection,
      resizeBounds,
    );
    const groupScaleX = initialGroupRect.width
      ? resizedGroupRect.width / initialGroupRect.width
      : 1;
    const groupScaleY = initialGroupRect.height
      ? resizedGroupRect.height / initialGroupRect.height
      : 1;

    setCurrentProcessingFile((prevFile) => {
      const prevTempFields = prevFile.document?.tempFields || {};
      const resizeField = (formField) => {
        const initialField = resizeSnapshot.get(formField.name);
        if (!initialField) return formField;

        const initialCanvasRect = initialRectsByName.get(formField.name);
        const nextCanvasRect = isGroupResize
          ? {
              left:
                resizedGroupRect.left +
                (initialCanvasRect.left - initialGroupRect.left) * groupScaleX,
              top:
                resizedGroupRect.top +
                (initialCanvasRect.top - initialGroupRect.top) * groupScaleY,
              width: initialCanvasRect.width * groupScaleX,
              height: initialCanvasRect.height * groupScaleY,
            }
          : resizedGroupRect;
        const nextPdfRect = canvasToPdfPoints(
          nextCanvasRect.left,
          nextCanvasRect.top,
          nextCanvasRect.width,
          nextCanvasRect.height,
          viewport || scaleFactor,
          72,
          currentDocument.actualHeight || 792,
          currentDocument.pageXMin || 0,
          currentDocument.pageYMin || 0,
        );
        const { newFontSize, fontSizes } = handleDynamicFontResize(
          nextPdfRect.width,
          nextPdfRect.height,
          initialField.fontSize || 10,
          initialField.value || "",
          initialField.fontSizes || [],
          initialField.value || "",
        );

        return {
          ...formField,
          width: nextPdfRect.width,
          height: nextPdfRect.height,
          x: nextPdfRect.x,
          y: nextPdfRect.y,
          fontSize: formField.autoFontSize ? newFontSize : formField.fontSize,
          fontSizes,
        };
      };
      const updatedFormFields = (prevTempFields.formFields || []).map(
        resizeField,
      );
      const updatedFieldsByName = new Map(
        updatedFormFields.map((formField) => [formField.name, formField]),
      );

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevTempFields,
            formFields: updatedFormFields,
            selectedExtraFields: (prevTempFields.selectedExtraFields || []).map(
              (selectedField) =>
                updatedFieldsByName.get(selectedField.name) || selectedField,
            ),
            selectedFormField: prevTempFields.selectedFormField
              ? updatedFieldsByName.get(
                  prevTempFields.selectedFormField.name,
                ) || prevTempFields.selectedFormField
              : null,
          },
        },
      };
    });
  } else if (moving && initialMousePosition?.insertedTextEditId) {
    const tempFields = currentProcessingFile?.document?.tempFields;
    const currentDocument = tempFields?.currentDocument;
    const editId = initialMousePosition.insertedTextEditId;
    const insertedText = tempFields?.pdfTextEdits?.find(
      (edit) =>
        edit.edit_id === editId &&
        edit.isEditorAdded === true &&
        edit.kind === "inserted" &&
        edit.action !== "delete" &&
        Number(edit.page) === Number(pageNumber),
    );
    if (
      !currentDocument ||
      !insertedText?.rect ||
      tempFields.selectedPdfTextItem?.id !== insertedText.target_item_id ||
      tempFields.editingInsertedTextId ||
      !inputPosition ||
      !initialInputSize
    ) {
      return;
    }

    const startX = Number(inputPosition.x);
    const startY = Number(inputPosition.y);
    const width = Number(initialInputSize.width);
    const height = Number(initialInputSize.height);
    const pageWidth = Number(currentDocument.actualWidth);
    const pageHeight = Number(currentDocument.actualHeight);
    const pageXMin = Number(currentDocument.pageXMin ?? 0);
    const pageYMin = Number(currentDocument.pageYMin ?? 0);
    const delta = canvasDeltaToPdfDelta(
      e.clientX - initialMousePosition.x,
      e.clientY - initialMousePosition.y,
      currentDocument.viewport,
      currentDocument.scaleFactor || scaleFactor || 1,
    );
    if (
      ![
        startX,
        startY,
        width,
        height,
        pageWidth,
        pageHeight,
        pageXMin,
        pageYMin,
        delta.x,
        delta.y,
      ].every(Number.isFinite) ||
      width < 0 ||
      height < 0 ||
      pageWidth <= 0 ||
      pageHeight <= 0
    ) {
      return;
    }

    // A click without movement must not reposition text or create undo history.
    // Returning to the drag origin also restores the exact original position.
    const hasMovement = delta.x !== 0 || delta.y !== 0;
    const finalX = hasMovement
      ? Math.max(
          pageXMin,
          Math.min(startX + delta.x, pageXMin + pageWidth - width),
        )
      : startX;
    const finalY = hasMovement
      ? Math.max(
          pageYMin,
          Math.min(startY + delta.y, pageYMin + pageHeight - height),
        )
      : startY;

    setCurrentProcessingFile((prevFile) => {
      const temp = prevFile?.document?.tempFields;
      if (
        !temp ||
        temp.currentDocument !== currentDocument ||
        temp.selectedPdfTextItem?.id !== insertedText.target_item_id ||
        temp.editingInsertedTextId
      ) {
        return prevFile;
      }
      const currentEdit = temp.pdfTextEdits?.find(
        (edit) =>
          edit.edit_id === editId &&
          edit.target_item_id === insertedText.target_item_id &&
          edit.isEditorAdded === true &&
          edit.kind === "inserted" &&
          edit.action !== "delete" &&
          Number(edit.page) === Number(pageNumber),
      );
      if (
        !currentEdit?.rect ||
        (currentEdit.rect.X === finalX && currentEdit.rect.Y === finalY)
      ) {
        return prevFile;
      }

      // Change geometry only; retain insertion identity, text, and formatting.
      const rect = { ...currentEdit.rect, X: finalX, Y: finalY };
      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...temp,
            pdfTextEdits: temp.pdfTextEdits.map((edit) =>
              edit === currentEdit ? { ...edit, rect } : edit,
            ),
            selectedPdfTextItem: { ...temp.selectedPdfTextItem, rect },
          },
        },
      };
    });
  } else if (moving) {
    if (
      !inputPosition ||
      !currentProcessingFile?.document?.tempFields?.selectedFormField
    )
      return;
    // Calculate movement delta
    const scaleFactor =
      currentProcessingFile?.document?.tempFields?.currentDocument?.pdfSize /
      currentProcessingFile?.document?.tempFields?.currentDocument?.actualWidth;

    // Translate the visual drag delta back into the PDF coordinate system.
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const { x: dx, y: dy } = canvasDeltaToPdfDelta(
      e.clientX - initialMousePosition.x,
      e.clientY - initialMousePosition.y,
      currentDocument.viewport,
      scaleFactor,
    );

    // Update position while keeping it within document bounds
    const finalX = Math.max(
      0,
      Math.min(
        inputPosition.x + dx,
        currentProcessingFile?.document?.tempFields?.currentDocument
          .actualWidth - initialInputSize.width,
      ),
    );
    const finalY = Math.max(
      0,
      Math.min(
        inputPosition.y + dy,
        currentProcessingFile?.document?.tempFields?.currentDocument
          .actualHeight - initialInputSize.height,
      ),
    );

    // A single functional update keeps the primary and extra selections in sync.
    // Map lookup replaces the old full-array find for every field (O(n * k)).
    setCurrentProcessingFile((prevFile) => {
      const temp = prevFile.document.tempFields;
      const primary = temp.selectedFormField;
      if (!primary) return prevFile;
      if (primary.x === finalX && primary.y === finalY) return prevFile;

      const offsetX = finalX - primary.x;
      const offsetY = finalY - primary.y;
      const updatedByName = new Map();
      const updatedPrimary = { ...primary, x: finalX, y: finalY };
      updatedByName.set(primary.name, updatedPrimary);
      const selectedExtraFields = (temp.selectedExtraFields || []).map(
        (field) => {
          const updated =
            field.name === primary.name
              ? updatedPrimary
              : { ...field, x: field.x + offsetX, y: field.y + offsetY };
          updatedByName.set(field.name, updated);
          return updated;
        },
      );

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...temp,
            formFields: temp.formFields.map(
              (field) => updatedByName.get(field.name) || field,
            ),
            selectedFormField: updatedPrimary,
            selectedExtraFields,
          },
        },
      };
    });
  } else if (isDrawing && !moving) {
    const currentX =
      (e.clientX - containerRect.left) /
      currentProcessingFile?.document?.tempFields?.currentDocument.scaleFactor;
    const currentY =
      (e.clientY - containerRect.top) /
      currentProcessingFile?.document?.tempFields?.currentDocument.scaleFactor;

    let width = currentX - startPoint.x;
    let height = currentY - startPoint.y;

    // Adjust for direction of dragging (invert Y-axis)
    const newX = width < 0 ? currentX : startPoint.x;
    const newY = height < 0 ? currentY : startPoint.y;

    const returnRect = {
      x: newX,
      y: newY,
      width: Math.abs(width),
      height: Math.abs(height),
    };

    const maxRight = width + newX;
    const maxTop = newY + height;

    const isOutsideTop = maxTop + 5 > pdfBottom;
    const isOutsideRight = maxRight > pdfRight;
    const isOutsideBottom = newY < 5;
    const isOutsideLeft = newX < pdfLeft;

    if (isOutsideTop || isOutsideRight || isOutsideBottom || isOutsideLeft)
      return;
    // Update the rectangle state with correct scaling and flipping
    setRect(returnRect);
  } else if (isSelecting && !moving) {
    // Use the exact same coordinate calculation as in mouseDown for freeSelectStartPoint
    const startX = e.clientX - containerRect.left;
    const startY = e.clientY - containerRect.top;

    const currentPoint = {
      x:
        startX /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      y:
        startY /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
    };

    let width = currentPoint.x - freeSelectStartPoint.x;
    let height = currentPoint.y - freeSelectStartPoint.y;

    // Adjust for direction of dragging (invert Y-axis)
    const newX = width < 0 ? currentPoint.x : freeSelectStartPoint.x;
    const newY = height < 0 ? currentPoint.y : freeSelectStartPoint.y;

    // Store selection rectangle in screen coordinate system (same as how fields are displayed)
    const newSelectionRect = {
      x: newX,
      y: newY,
      width: Math.abs(width),
      height: Math.abs(height),
    };

    const maxRight = width + newX;
    const maxTop = newY + height;

    const isOutsideTop = maxTop + 5 > pdfBottom;
    const isOutsideRight = maxRight > pdfRight;
    const isOutsideBottom = newY < 5;
    const isOutsideLeft = newX < pdfLeft;

    if (isOutsideTop || isOutsideRight || isOutsideBottom || isOutsideLeft)
      return;
    // Update the rectangle state with correct scaling and flipping
    setSelectionRect(newSelectionRect);

    //? COMPARE IN DISPLAY COORDINATE SYSTEM INSTEAD
    // Instead of converting selection to field coordinates, convert fields to display coordinates
    // This way we compare in the same coordinate system as visual display
    const displayScaleFactor =
      currentProcessingFile?.document?.tempFields?.currentDocument
        ?.scaleFactor || 1;
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};

    // Selection rectangle is in container coordinates (already divided by scaleFactor)
    // Convert to display pixels for comparison
    const selectionLeft = newSelectionRect.x * displayScaleFactor;
    const selectionTop = newSelectionRect.y * displayScaleFactor;
    const selectionRight =
      (newSelectionRect.x + newSelectionRect.width) * displayScaleFactor;
    const selectionBottom =
      (newSelectionRect.y + newSelectionRect.height) * displayScaleFactor;

    setCurrentProcessingFile((prevFile) => {
      const temp = prevFile.document.tempFields;
      const previous = temp.selectedExtraFields || [];

      // Preserve master's selections from other pages.
      const otherPagesSelection = previous.filter(
        (field) => Number(field?.page_num ?? field?.page) !== pageNumber,
      );

      // Preserve our cached geometry.
      const geometry = getSelectionGeometry(
        freeSelectStartPoint,
        temp.formFields,
        currentDocument,
        pageNumber,
      );

      const intersects = (fieldRect) =>
        fieldRect.left < selectionRight &&
        fieldRect.left + fieldRect.width > selectionLeft &&
        fieldRect.top < selectionBottom &&
        fieldRect.top + fieldRect.height > selectionTop;

      const currentPageSelection = geometry
        .filter(({ rect }) => intersects(rect))
        .map(({ field }) => field);

      const selectedExtraFields = [
        ...otherPagesSelection,
        ...currentPageSelection,
      ];

      let selectedFormField = temp.selectedFormField;

      if (selectedFormField) {
        const primaryRect = pdfRectToCanvasRect(
          selectedFormField,
          currentDocument.viewport || currentDocument.actualHeight || 792,
          displayScaleFactor,
          currentDocument.pageXMin || 0,
          currentDocument.pageYMin || 0,
        );

        if (!intersects(primaryRect)) selectedFormField = null;
      }

      // Skip unnecessary state updates.
      if (
        selectedFormField === temp.selectedFormField &&
        previous.length === selectedExtraFields.length &&
        previous.every((field, index) => field === selectedExtraFields[index])
      ) {
        return prevFile;
      }

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...temp,
            selectedExtraFields,
            selectedFormField,
          },
        },
      };
    });
  }
};
